import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Tag, X, Plus, Edit2, Check } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// Simple global cache so we only fetch available tags once per session
let globalTagCache = null;
let globalTagCachePromise = null;

const fetchTagsFromServer = async () => {
  try {
    const response = await api.get('/tags');
    return response.tags || [];
  } catch (error) {
    console.error('Failed to load tags:', error);
    return [];
  }
};

const getCachedTags = async (forceRefresh = false) => {
  if (!forceRefresh && globalTagCache) {
    return globalTagCache;
  }

  if (!forceRefresh && globalTagCachePromise) {
    return globalTagCachePromise;
  }

  globalTagCachePromise = fetchTagsFromServer()
    .then((tags) => {
      globalTagCache = tags;
      return tags;
    })
    .finally(() => {
      globalTagCachePromise = null;
    });

  return globalTagCachePromise;
};

/**
 * TagService - A reusable component for managing tags across entities and attachments
 * Provides tag selection, creation, and display with consistent styling
 */
const TagService = ({
  entityType,
  entityId,
  initialTags = [],
  onChange,
  readonly = false,
  maxTags = 10,
  placeholder = 'Add tags...',
}) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const safeT = (key, fallback) => t(key, fallback);

  const [tags, setTags] = useState(initialTags);
  const [availableTags, setAvailableTags] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [tagSources, setTagSources] = useState({});
  const [loadingEntityTags, setLoadingEntityTags] = useState(false);
  const inputRef = useRef(null);

  const loadAvailableTags = useCallback(async (options = {}) => {
    const { forceRefresh = false } = options;
    const tags = await getCachedTags(forceRefresh);
    setAvailableTags(tags);
    return tags;
  }, []);

  // Load available tags from the system (only once on mount)
  useEffect(() => {
    loadAvailableTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only load once on mount, uses cached version

  // Focus input when shown
  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showInput]);

  // Initialize tags from initialTags
  useEffect(() => {
    setTags(initialTags);
  }, [initialTags]);

  // Load tag associations for entity (includes auto/manual source)
  useEffect(() => {
    const loadEntityTags = async () => {
      if (!entityType || !entityId) return;
      setLoadingEntityTags(true);
      try {
        const response = await api.get(`/tags/entity/${entityType}/${entityId}`);
        const tagList = response.tags || [];
        const tagNames = tagList.map((tag) => tag.name);
        const sources = tagList.reduce((acc, tag) => {
          acc[tag.name] = tag.source || 'manual';
          return acc;
        }, {});
        setTags(tagNames);
        setTagSources(sources);
      } catch (error) {
        console.error('Failed to load entity tags:', error);
      } finally {
        setLoadingEntityTags(false);
      }
    };

    loadEntityTags();
  }, [entityType, entityId]);

  const getTagStyle = (tagName) => {
    const tag = availableTags.find((t) => t.name === tagName);
    if (!tag) return {};

    return {
      backgroundColor: tag.backgroundColor || (isDark ? '#374151' : '#F3F4F6'),
      color: tag.textColor || (isDark ? '#E5E7EB' : '#374151'),
      fontWeight: tag.fontWeight || 'normal',
      borderColor: tag.borderColor || 'transparent',
    };
  };

  const filteredSuggestions = availableTags.filter(
    (tag) =>
      tag.name.toLowerCase().includes(inputValue.toLowerCase()) &&
      !tags.includes(tag.name)
  );

  const handleAddTag = useCallback(
    async (tagName, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (!tagName || tags.includes(tagName) || tags.length >= maxTags) {
        return;
      }

      setLoading(true);
      try {
        // Create tag if it doesn't exist
        if (!availableTags.find((t) => t.name === tagName)) {
          try {
            await api.post('/tags', { name: tagName });
            globalTagCache = null; // Invalidate cache so everyone sees the new tag
            await loadAvailableTags({ forceRefresh: true });
          } catch (error) {
            console.error('Failed to create tag:', error);
          }
        }

        // Save tag association
        if (entityType && entityId) {
          await api.post(`/tags/associate`, {
            tagName,
            entityType,
            entityId,
          });
        }

        const newTags = [...tags, tagName];
        setTags(newTags);
        setTagSources((prev) => ({ ...prev, [tagName]: 'manual' }));
        setInputValue('');
        setShowSuggestions(false);
        setShowInput(false);

        if (onChange) {
          onChange(newTags);
        }
      } catch (error) {
        console.error('Failed to add tag:', error);
      } finally {
        setLoading(false);
      }
    },
    [tags, maxTags, availableTags, entityType, entityId, onChange, loadAvailableTags]
  );

  const handleRemoveTag = useCallback(
    async (tagName, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      console.log('🗑️ Removing tag:', tagName);
      console.log('📋 Context:', { entityType, entityId, tagName });
      setLoading(true);
      
      try {
        // Remove tag association from backend first
        if (entityType && entityId) {
          console.log('📡 Calling API DELETE /tags/associate with:', {
            tagName,
            entityType,
            entityId
          });
          
          const response = await api.delete(`/tags/associate`, {
            tagName,
            entityType,
            entityId,
          });
          
          console.log('✅ Tag association removed from backend. Response:', response);
        }

        // Update local state after successful API call
        const newTags = tags.filter((t) => t !== tagName);
        setTags(newTags);
        setTagSources((prev) => {
          const next = { ...prev };
          delete next[tagName];
          return next;
        });
        console.log('✅ Local state updated, remaining tags:', newTags);

        if (onChange) {
          onChange(newTags);
        }
      } catch (error) {
        console.error('❌ Failed to remove tag association. Full error:', error);
        console.error('❌ Error response:', error.response?.data);
        console.error('❌ Error status:', error.response?.status);
        alert(`Failed to remove tag: ${error.response?.data?.error || error.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    },
    [tags, entityType, entityId, onChange]
  );

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && inputValue.trim()) {
      event.preventDefault();
      event.stopPropagation();
      handleAddTag(inputValue.trim(), event);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setShowSuggestions(false);
      setInputValue('');
      setShowInput(false);
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    setShowSuggestions(value.length > 0);
  };

  const handleAddClick = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setShowInput(true);
    setEditMode(false); // Exit edit mode when adding
  };

  const handleCancelAdd = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setShowInput(false);
    setInputValue('');
    setShowSuggestions(false);
  };

  const toggleEditMode = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setEditMode(!editMode);
    setShowInput(false); // Close input when toggling edit
  };

  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-600';
  const bgPrimary = isDark ? 'bg-gray-800' : 'bg-white';
  const bgSecondary = isDark ? 'bg-gray-700' : 'bg-gray-100';
  const borderColor = isDark ? 'border-gray-700' : 'border-gray-300';

  return (
    <div className="space-y-2">
      {/* Tags Display Area */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Display existing tags */}
        {tags.map((tagName) => {
          const style = getTagStyle(tagName);
          const source = tagSources[tagName];
          return (
            <span
              key={tagName}
              className="inline-flex items-center gap-0.5 rounded-full px-2.5 py-0.5 text-xs font-medium border transition-all"
              style={{ ...style, transform: 'scale(0.9)' }}
            >
              <Tag className="h-2.5 w-2.5" />
              {tagName}
              {source === 'auto' && (
                <span className="ml-1 rounded-full bg-indigo-600/20 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 dark:text-indigo-300">
                  {safeT('tags.auto', 'Auto')}
                </span>
              )}
              {/* Show X button only in edit mode */}
              {!readonly && editMode && (
                <button
                  type="button"
                  onClick={(e) => handleRemoveTag(tagName, e)}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="ml-0.5 rounded-full hover:bg-black/20 dark:hover:bg-white/20 p-0.5 transition-colors cursor-pointer"
                  disabled={loading}
                  title="Remove tag"
                  style={{ pointerEvents: 'auto' }}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </span>
          );
        })}

        {/* Action Buttons */}
        {!readonly && !showInput && (
          <>
            {/* Add Tag Button - Only show if not at max */}
            {tags.length < maxTags && (
              <button
                type="button"
                onClick={(e) => handleAddClick(e)}
                className={`inline-flex items-center gap-0.5 rounded-full px-2.5 py-0.5 text-xs font-medium border ${borderColor} ${bgSecondary} ${textSecondary} hover:bg-opacity-80 transition-colors`}
                style={{ transform: 'scale(0.9)' }}
                disabled={loading}
                title="Add new tag"
              >
                <Plus className="h-2.5 w-2.5" />
                <span className="text-xs">{safeT('tags.add', 'Add Tag')}</span>
              </button>
            )}
            
            {/* Edit Button - Only show if there are tags */}
            {tags.length > 0 && (
              <button
                type="button"
                onClick={(e) => toggleEditMode(e)}
                className={`inline-flex items-center gap-0.5 rounded-full px-2.5 py-0.5 text-xs font-medium border ${
                  editMode 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
                    : `${borderColor} ${bgSecondary} ${textSecondary}`
                } hover:bg-opacity-80 transition-colors`}
                style={{ transform: 'scale(0.9)' }}
                disabled={loading}
                title={editMode ? "Done editing" : "Edit tags"}
              >
                {editMode ? (
                  <>
                    <Check className="h-2.5 w-2.5" />
                    <span className="text-xs">{safeT('tags.done', 'Done')}</span>
                  </>
                ) : (
                  <>
                    <Edit2 className="h-2.5 w-2.5" />
                    <span className="text-xs">{safeT('tags.edit', 'Edit')}</span>
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>

      {/* Input Box - Only visible when adding */}
      {!readonly && showInput && tags.length < maxTags && (
        <div className="relative">
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (inputValue.trim()) {
                handleAddTag(inputValue.trim(), e);
              }
            }}
          >
            <div className={`flex items-center gap-2 rounded-lg border-2 ${isDark ? 'border-blue-500' : 'border-blue-400'} ${bgPrimary} px-3 py-2 shadow-sm`}>
              <Tag className={`h-3.5 w-3.5 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => inputValue && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder={placeholder}
                className={`flex-1 bg-transparent outline-none text-sm ${textPrimary}`}
                disabled={loading}
              />
            <div className="flex items-center gap-1">
              {inputValue && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddTag(inputValue.trim(), e);
                  }}
                  className="text-green-600 hover:text-green-700 dark:text-green-400 p-1 rounded hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
                  disabled={loading}
                  title="Add tag"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => handleCancelAdd(e)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                disabled={loading}
                title="Cancel"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div
              className={`absolute z-10 mt-1 w-full rounded-lg border ${borderColor} ${bgPrimary} shadow-lg max-h-48 overflow-y-auto`}
            >
              {filteredSuggestions.map((tag) => {
                const style = getTagStyle(tag.name);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddTag(tag.name, e);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors`}
                  >
                    <span
                      className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium border"
                      style={{ ...style, transform: 'scale(0.9)' }}
                    >
                      <Tag className="h-2.5 w-2.5" />
                      {tag.name}
                    </span>
                    {tag.usageCount > 0 && (
                      <span className="text-xs text-gray-500 ml-auto">
                        {tag.usageCount} <span className="text-xs sm:text-sm">{safeT('tags.uses', 'uses')}</span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          </form>
        </div>
      )}

      {/* Tag limit indicator */}
      {!readonly && tags.length >= maxTags && (
        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <Tag className="h-2.5 w-2.5" />
          <span className="text-xs">{safeT('tags.maxReached', `Maximum ${maxTags} tags reached`)}</span>
        </p>
      )}

      {loadingEntityTags && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {safeT('tags.loading', 'Loading tags...')}
        </p>
      )}
    </div>
  );
};

TagService.propTypes = {
  entityType: PropTypes.string,
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialTags: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  readonly: PropTypes.bool,
  maxTags: PropTypes.number,
  placeholder: PropTypes.string,
};

export default TagService;

